<script setup>
import { computed, onMounted, ref, useAttrs } from 'vue'

/**
 * Branded text input. Accepts the standard v-model + an optional `:error`
 * prop that auto-wires the WCAG-required aria attributes.
 *
 * When `:error` is set:
 *   - `aria-invalid="true"` flags the field as failing validation
 *   - `aria-describedby` points at the sibling InputError's id (derived
 *     from the input's own `id` by appending `-error`)
 *
 * Callers pair this with `<InputError :id="`${inputId}-error`" :message />`
 * so the screen reader announces the error text when focus lands on the
 * invalid field.
 */
defineProps({
    modelValue: {
        type: [String, Number],
        default: '',
    },
    error: {
        type: String,
        default: null,
    },
})

defineEmits(['update:modelValue'])

const input = ref(null)

const attrs = useAttrs()

// Derive the error-message id from the input's own id so the
// aria-describedby link is automatic. Callers that pass a non-default
// error-id can override via `aria-describedby` directly (Vue merges
// $attrs onto the root element).
const errorId = computed(() => (attrs.id ? `${attrs.id}-error` : undefined))

onMounted(() => {
    if (input.value?.hasAttribute('autofocus')) {
        input.value.focus()
    }
})

defineExpose({ focus: () => input.value?.focus() })
</script>

<template>
    <!--
        Background: bg-surface-container-high (#2a2a2c) - bumped 2 MD3 steps
        up from `-low` (#1c1b1d) which barely contrasted against the page
        surface (#131315). Borders are zinc-700 (was -800) for a sharper edge.
        Focus ring keeps the primary-container orange highlight.
    -->
    <input
        class="bg-surface-container-high text-on-surface focus:border-primary-container focus:ring-primary-container/70 w-full rounded-md border p-4 pr-12 transition-all placeholder:text-zinc-500 focus:ring-2 focus:outline-none"
        :class="error ? 'border-error' : 'border-zinc-700'"
        ref="input"
        :value="modelValue"
        :aria-invalid="error ? 'true' : undefined"
        :aria-describedby="error ? errorId : undefined"
        @input="$emit('update:modelValue', $event.target.value)"
    />
</template>
